// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Dialog component for merging two customers in the Hardfin system.
 * Provides a multi-step interface with customer selection, field comparison, and integration validation.
 */
import * as Sentry from '@sentry/react';
import * as z from 'zod';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { LoadingIndicator } from '@/components/navigation-layout';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useToast } from '@/contexts/toast-context';
import {
  MergeCustomerParameters,
  mergeCustomer,
} from '@/customer/api/merge-customer';
import { Customer } from '@/customer/api/types';
import { CustomerSelectionList } from '@/customer/components/customer-selection-list';
import { formatNumber } from '@/utilities/number';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  alpha,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';

const mergeFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  billingContact: z.object({
    name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
  }),
  billingAddress: z.string().nullable(),
  comment: z.string().nullable(),
  externalId: z.string().nullable(),
  isArchived: z.boolean(),
});

type MergeFormData = z.infer<typeof mergeFormSchema>;

interface Props {
  isOpen: boolean;
  customer: Customer;
  onClose: () => void;
}

const steps = ['Select customer to merge', 'Select data fields to merge'];

const TOAST_DURATION_MS = 5000;
const ARCHIVED_DISPLAY_VALUE = 'Archived';
const ACTIVE_DISPLAY_VALUE = 'Active';
// Search field (40px) + stack spacing (16px) + fixed list height (450px),
// so the loading fallback occupies the same space as the loaded list
const SELECTION_PANEL_HEIGHT_PX = 506;
const RETAINED_NOT_FOUND_MESSAGE =
  'This customer no longer exists. It may have been deleted or merged by someone else. Refresh the page and try again.';
const REMOVED_NOT_FOUND_MESSAGE =
  'The selected customer no longer exists. It may have been deleted or merged by someone else. Go back and select another customer.';
const VALIDATION_ERROR_MESSAGE =
  'Some required fields are missing. Select a value for each required field.';
const CRM_LINKED_MESSAGE =
  'Both customers are linked to CRM systems. Please merge them in your CRM first and then merge in Hardfin.';

/**
 * Type representing valid paths for merge comparison fields.
 */
type MergeFieldPath =
  | 'name'
  | 'billingContact.name'
  | 'billingContact.email'
  | 'billingContact.phone'
  | 'billingAddress'
  | 'comment'
  | 'externalId'
  | 'isArchived';

type FieldSource = 'primary' | 'selected';

interface FieldDefinition {
  label: string;
  path: MergeFieldPath;
  required: boolean;
  // Hide the comparison row entirely when both customers hold the same value
  hideWhenEqual?: boolean;
}

const fields: FieldDefinition[] = [
  { label: 'Customer name', path: 'name', required: true },
  {
    label: 'Billing contact name',
    path: 'billingContact.name',
    required: false,
  },
  {
    label: 'Billing contact email',
    path: 'billingContact.email',
    required: false,
  },
  {
    label: 'Billing contact phone',
    path: 'billingContact.phone',
    required: false,
  },
  { label: 'Billing address', path: 'billingAddress', required: false },
  { label: 'Comment', path: 'comment', required: false },
  { label: 'External ID', path: 'externalId', required: false },
  {
    label: 'Archived',
    path: 'isArchived',
    required: false,
    hideWhenEqual: true,
  },
];

function getNestedValue(obj: Customer, path: string): string | null {
  const parts = path.split('.');
  let value: unknown = obj;
  for (const part of parts) {
    if (!value || typeof value !== 'object') return null;
    value = (value as Record<string, unknown>)[part];
  }
  if (typeof value === 'string') {
    return value;
  }
  return null;
}

/**
 * Returns the display value for a merge field on a customer.
 */
function getFieldValue(
  customer: Customer,
  path: MergeFieldPath
): string | null {
  if (path === 'isArchived') {
    return customer.isArchived ? ARCHIVED_DISPLAY_VALUE : ACTIVE_DISPLAY_VALUE;
  }
  return getNestedValue(customer, path);
}

/**
 * Returns whether both customers are linked to a CRM system, which blocks the
 * merge until one is unlinked or merged in the CRM.
 */
function bothLinkedToCRM(primary: Customer, selected: Customer): boolean {
  return (
    primary.integrations.some((integration) => integration.link !== null) &&
    selected.integrations.some((integration) => integration.link !== null)
  );
}

interface SelectCustomerStepProps {
  customer: Customer;
  onCustomerSelect: (customer: Customer) => void;
  mergeError: string | null;
}

function SelectCustomerStep(props: SelectCustomerStepProps) {
  return (
    <Stack spacing={3}>
      <Grid container sx={{ px: 8 }}>
        <Grid item xs={6} sx={{ display: 'flex', pr: 2 }}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.2),
              }}
            >
              <Typography variant="h6">{props.customer.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {props.customer.billingContact?.email}
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Current associations
                </Typography>
                <Stack gap={1} direction="column" sx={{ flexGrow: 1 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {formatNumber(props.customer.activeProjectCt)}
                    </Typography>
                    <Typography variant="body2">active projects</Typography>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {formatNumber(props.customer.locationCount)}
                    </Typography>
                    <Typography variant="body2">customer locations</Typography>
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {formatNumber(props.customer.assetsAtLocation)}
                    </Typography>
                    <Typography variant="body2">assets on location</Typography>
                  </Paper>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', pl: 2 }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" gutterBottom>
              Select customer to merge with {props.customer.name}
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <Suspense
                fallback={
                  <Box
                    sx={{
                      height: SELECTION_PANEL_HEIGHT_PX,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <LoadingIndicator />
                  </Box>
                }
              >
                <SupportErrorBoundary message="An error occurred while loading companies.">
                  <CustomerSelectionList
                    onCustomerSelect={props.onCustomerSelect}
                    excludeCustomerId={props.customer.id}
                  />
                </SupportErrorBoundary>
              </Suspense>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {props.mergeError && (
        <Alert severity="error">
          <AlertTitle>Cannot merge customers</AlertTitle>
          {props.mergeError}
        </Alert>
      )}
    </Stack>
  );
}

interface CompareAndMergeStepProps {
  primaryCustomer: Customer;
  selectedCustomer: Customer;
  fieldSelections: Record<string, FieldSource>;
  onFieldSelectionChange: (
    fieldPath: MergeFieldPath,
    source: FieldSource
  ) => void;
}

function CompareAndMergeStep(props: CompareAndMergeStepProps) {
  const visibleFields = fields.filter((field) => {
    if (!field.hideWhenEqual) {
      return true;
    }
    return (
      getFieldValue(props.primaryCustomer, field.path) !==
      getFieldValue(props.selectedCustomer, field.path)
    );
  });

  return (
    <Paper variant="outlined" sx={{ overflow: 'auto', flexGrow: 1, pb: 1 }}>
      <Stack>
        <Box
          display="grid"
          gridTemplateColumns="2fr 2fr 2fr 3fr"
          gap={2}
          sx={{ px: 2, py: 1.5 }}
          alignItems="center"
        >
          <Typography variant="subtitle2">Field</Typography>
          <Box>
            <Typography variant="subtitle2">
              {props.primaryCustomer.name}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">
              {props.selectedCustomer.name}
            </Typography>
          </Box>
          <Typography variant="subtitle2">Merged result</Typography>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {visibleFields.map((field, index) => {
          const primaryValue = getFieldValue(props.primaryCustomer, field.path);
          const selectedValue = getFieldValue(
            props.selectedCustomer,
            field.path
          );
          const selection = props.fieldSelections[field.path] ?? 'primary';
          const mergedValue =
            selection === 'selected' ? selectedValue : primaryValue;
          const showRadio: boolean =
            (Boolean(primaryValue) || Boolean(selectedValue)) &&
            primaryValue !== selectedValue;
          return (
            <Box key={field.path}>
              <Box
                display="grid"
                sx={{ px: 2 }}
                gridTemplateColumns="2fr 2fr 2fr 3fr"
                gap={2}
                alignItems="center"
              >
                <Typography variant="body2">
                  {field.label}
                  {field.required && (
                    <Typography component="span" color="error.main">
                      *
                    </Typography>
                  )}
                </Typography>

                <Box>
                  {showRadio ? (
                    <FormControlLabel
                      control={
                        <Radio
                          checked={selection === 'primary'}
                          onChange={() =>
                            props.onFieldSelectionChange(field.path, 'primary')
                          }
                        />
                      }
                      label={
                        <Typography variant="body2" component="span">
                          {primaryValue ?? (
                            <Typography component="span" color="text.secondary">
                              Not set
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  ) : primaryValue ? (
                    <Stack direction="row" alignItems="center">
                      <Box sx={{ p: 2 }}></Box>
                      {primaryValue}
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center">
                      <Box sx={{ p: 2 }}></Box>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        Not set
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <Box>
                  {showRadio ? (
                    <FormControlLabel
                      control={
                        <Radio
                          checked={selection === 'selected'}
                          onChange={() =>
                            props.onFieldSelectionChange(field.path, 'selected')
                          }
                        />
                      }
                      label={
                        <Typography variant="body2" component="span">
                          {selectedValue ?? (
                            <Typography component="span" color="text.secondary">
                              Not set
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  ) : selectedValue ? (
                    <Stack direction="row" alignItems="center">
                      <Box sx={{ p: 2 }}></Box>
                      {selectedValue}
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center">
                      <Box sx={{ p: 2 }}></Box>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        Not set
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <Typography variant="body2" color="text.primary">
                  {mergedValue || (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      Not set
                    </Typography>
                  )}
                </Typography>
              </Box>
              {index < visibleFields.length - 1 && (
                <Divider sx={{ gridColumn: '1/-1', my: 1 }} />
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

export function MergeCustomerDialog(props: Props) {
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const { showToast } = useToast();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldSelections, setFieldSelections] = useState<
    Record<string, FieldSource>
  >({});

  const { handleSubmit, setValue } = useForm<MergeFormData>({
    resolver: zodResolver(mergeFormSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: props.customer.name,
      billingContact: {
        name: props.customer.billingContact?.name ?? null,
        email: props.customer.billingContact?.email ?? null,
        phone: props.customer.billingContact?.phone ?? null,
      },
      billingAddress: props.customer.billingAddress,
      comment: props.customer.comment,
      externalId: props.customer.externalId,
      isArchived: props.customer.isArchived,
    },
  });

  const mergeCustomerMutation = useMutation({
    mutationFn: (parameters: MergeCustomerParameters) =>
      mergeCustomer(props.customer.key, parameters),
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.customer_merge_removed_not_found
      ) {
        setErrorMessage(REMOVED_NOT_FOUND_MESSAGE);
        return;
      }
      // The backend returns customer_invalid_key when the retained customer
      // is gone; the self-merge and CRM-linked cases that share the code are
      // prevented by the dialog's own guards
      if (apiError.apiStatusCode === ERROR_CODE_MAPPING.customer_invalid_key) {
        setErrorMessage(RETAINED_NOT_FOUND_MESSAGE);
        return;
      }
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setErrorMessage(curated);
        return;
      }
      if (!apiError.sentToSentry) {
        Sentry.captureException(error);
      }
    },
    onSuccess: () => {
      showToast({
        duration: TOAST_DURATION_MS,
        element: <Box>Merge complete, now viewing the merged record</Box>,
      });
      // A customer merge migrates projects, contacts, invoices, payments,
      // credit memos, purchase orders, and locations to the retained
      // customer, so every cached query family may be stale
      queryClient.invalidateQueries();
      resetAndClose();
    },
  });

  const isUnhandledError = mergeCustomerMutation.isError && errorMessage === '';

  const applyFieldFromCustomer = (path: MergeFieldPath, customer: Customer) => {
    if (path === 'isArchived') {
      setValue('isArchived', customer.isArchived);
      return;
    }
    setValue(path, getNestedValue(customer, path));
  };

  const handleCustomerSelect = (customer: Customer) => {
    if (bothLinkedToCRM(props.customer, customer)) {
      setMergeError(CRM_LINKED_MESSAGE);
      return;
    }
    setMergeError(null);

    const initialSelections: Record<string, FieldSource> = {};
    fields.forEach((field) => {
      const primaryValue = getFieldValue(props.customer, field.path);
      const selectedValue = getFieldValue(customer, field.path);
      const source: FieldSource =
        !primaryValue && selectedValue ? 'selected' : 'primary';
      initialSelections[field.path] = source;
      applyFieldFromCustomer(
        field.path,
        source === 'primary' ? props.customer : customer
      );
    });
    setFieldSelections(initialSelections);
    setSelectedCustomer(customer);
    setActiveStep(1);
  };

  const handleFieldSelectionChange = (
    fieldPath: MergeFieldPath,
    source: FieldSource
  ) => {
    setFieldSelections((prev) => ({ ...prev, [fieldPath]: source }));

    const sourceCustomer =
      source === 'primary' ? props.customer : selectedCustomer;
    if (sourceCustomer) {
      applyFieldFromCustomer(fieldPath, sourceCustomer);
    }
  };

  const handleMergeSubmit = (data: MergeFormData) => {
    if (!selectedCustomer) {
      return;
    }
    setErrorMessage('');
    mergeCustomerMutation.mutate({
      removedCustomerId: selectedCustomer.id,
      name: data.name,
      billingAddress: data.billingAddress ?? null,
      comment: data.comment ?? null,
      externalId: data.externalId ?? null,
      isArchived: data.isArchived,
      billingContact: {
        name: data.billingContact?.name ?? null,
        email: data.billingContact?.email ?? null,
        phone: data.billingContact?.phone ?? null,
      },
    });
  };

  const handleInvalidSubmit = () => {
    setErrorMessage(VALIDATION_ERROR_MESSAGE);
  };

  const resetAndClose = () => {
    setActiveStep(0);
    setSelectedCustomer(null);
    setMergeError(null);
    setFieldSelections({});
    setErrorMessage('');
    // Reset the mutation so a handled error cannot resurface as the generic
    // support snackbar if the dialog is mounted persistently
    mergeCustomerMutation.reset();
    props.onClose();
  };

  const handleClose = () => {
    if (mergeCustomerMutation.isPending) {
      return;
    }
    resetAndClose();
  };

  const handleBack = () => {
    setActiveStep(0);
    setSelectedCustomer(null);
    setMergeError(null);
    setErrorMessage('');
    // Clear the error state so a handled failure does not resurface as the
    // generic support snackbar once errorMessage is cleared
    mergeCustomerMutation.reset();
  };

  return (
    <Dialog open={props.isOpen} onClose={handleClose} maxWidth="lg" fullWidth>
      <SupportSnackbar
        isOpen={isUnhandledError}
        message="An error occurred while merging the customers"
        onClose={mergeCustomerMutation.reset}
      />
      <DialogTitle>
        Merge customers
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Select another customer to merge into {props.customer.name}
        </Typography>
      </DialogTitle>
      <Box sx={{ px: 6 }}>
        <Alert severity="warning">
          <Typography
            variant="inherit"
            component="span"
            sx={{ fontWeight: 'bold' }}
          >
            <Typography
              variant="inherit"
              component="span"
              sx={{ textDecoration: 'underline' }}
            >
              {props.customer.name} will be preserved
            </Typography>{' '}
            and the
            <Typography
              variant="inherit"
              component="span"
              sx={{ textDecoration: 'underline' }}
            >
              {' '}
              other customer will be permanently deleted
            </Typography>
            .
          </Typography>{' '}
          You will select which information you want to keep (name, address,
          etc). All other associations will be migrated automatically (projects,
          locations, assets, etc).
        </Alert>
      </Box>
      <form onSubmit={handleSubmit(handleMergeSubmit, handleInvalidSubmit)}>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Stepper
              alternativeLabel
              activeStep={activeStep}
              sx={{
                '& .MuiStepIcon-root': {
                  fontSize: '2.25rem', // 1.5x the default size (1.5rem * 1.5 = 2.25rem)
                },
                '& .MuiStepIcon-text': {
                  fontWeight: 'bold',
                },
                '& .MuiStepConnector-line': {
                  borderTopWidth: '1px', // Make the line slightly thicker to match larger icons
                },
                '& .MuiStepConnector-root': {
                  top: '18px', // Adjust to align with center of enlarged icons (2.25rem / 2 = 1.125rem ≈ 18px)
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {activeStep === 0 && (
            <SelectCustomerStep
              customer={props.customer}
              onCustomerSelect={handleCustomerSelect}
              mergeError={mergeError}
            />
          )}

          {activeStep === 1 && selectedCustomer && (
            <CompareAndMergeStep
              primaryCustomer={props.customer}
              selectedCustomer={selectedCustomer}
              fieldSelections={fieldSelections}
              onFieldSelectionChange={handleFieldSelectionChange}
            />
          )}

          {errorMessage !== '' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', m: 1 }}>
          {activeStep === 0 ? (
            <Button onClick={handleClose}>Cancel</Button>
          ) : (
            <>
              <Button onClick={handleBack}>Back</Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={mergeCustomerMutation.isPending}
              >
                Confirm merge
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
